import { getAxiosClient } from './api';
import { getMetadata } from '~loader/loader-utils';
import editorConfig from '~config/editorConfig';
import { EDITOR_MODE_GLOBAL_TEMPLATE_MANAGER } from '~config/editorConstants';
import { getState, modifyState } from '~src/state';
import { isLoggedUserGlobalEditor } from '~src/user';

const TEMPLATE_IMPORT_POLL_INTERVAL_MS = 3000;
const TEMPLATE_IMPORT_TIMEOUT_MS = 15 * 60 * 1000;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const createTemplateAndPagesRelation = async (templateId, pagesIds) => {
  const httpClient = await getAxiosClient();

  let tableName;
  const isGlobalEditor = await isLoggedUserGlobalEditor();

  if (isGlobalEditor) {
    tableName = 'editor_templates_default';
  } else {
    tableName = 'editor_templates';
  }

  const url = `${process.env.PARSE_API_URL}/${tableName}/${templateId}`;
  const method = 'PUT';
  const data = {};

  data.relations = [
    {
      class: `${tableName}_pages`,
      column: `${tableName}_pages`,
      toIds: pagesIds,
    },
  ];

  return httpClient({ url, method, data: { data }, responseType: 'json' });
};

async function saveTemplatePages(templateId, firstSave = false) {
  const httpClient = await getAxiosClient();
  const format = getState('selected.formato');
  const formatDefault = getState('selected.formatoDefault');

  let url;
  let method;

  if (firstSave === false) {
    const currentPageIndex = getState('currentPageIndex');

    try {
      // salvo la pagina attuale
      const savedPage = JSON.stringify(
        window.canvas.toJSON(editorConfig.additionalJsonProperties)
      );
      modifyState(`pages[${currentPageIndex}].canvasJson`, savedPage);
    } catch (error) {
      modifyState(`pages[${currentPageIndex}].canvasJson`, '');
    }
  }

  const pagesIds = [];
  // let savedPagesCount = 0;
  const savePagePromises = [];
  const pages = getState('pages');

  const isGlobalEditor = await isLoggedUserGlobalEditor();

  for (let page of pages) {
    const data = {};

    data.canvasJson = page.canvasJson;
    data.order = page.order;

    if (page.animations) {
      if (typeof page.animations === 'string') {
        data.animations = page.animations;
      } else {
        data.animations = JSON.stringify(page.animations);
      }
    }

    if (isGlobalEditor) {
      data.isGlobal = true;
    }

    const editorMode = getState('editorMode');

    if (editorMode === EDITOR_MODE_GLOBAL_TEMPLATE_MANAGER) {
      data.editor_templates_default_pointer = templateId;
    } else {
      data.editor_templates_pointer = templateId;
    }

    if (format) {
      data.editor_project_formats_pointer = format.objectId;
    }

    if (formatDefault) {
      data.editor_project_formats_default_pointer = formatDefault.objectId;
    }

    let tableName;

    if (editorMode === EDITOR_MODE_GLOBAL_TEMPLATE_MANAGER) {
      tableName = 'editor_templates_default_pages';
    } else {
      tableName = 'editor_templates_pages';
    }

    if (page.objectId) {
      data.objectId = page.objectId;
      url = `${process.env.PARSE_API_URL}/${tableName}/${page.objectId}`;
      method = 'PUT';
    } else {
      url = `${process.env.PARSE_API_URL}/${tableName}`;
      method = 'POST';
    }

    const promise = httpClient({
      url,
      method,
      data: { data },
      responseType: 'json',
    });

    savePagePromises.push(promise);
  }

  const responses = await Promise.all(savePagePromises);

  responses.forEach((response, index) => {
    const pageId = response.data.data.objectId;
    modifyState(`pages[${index}].objectId`, pageId);
    pagesIds.push(pageId);
  });

  return pagesIds;
}

export const saveEditorTemplate = async (firstSave = false) => {
  const httpClient = await getAxiosClient();

  const metadata = getMetadata();

  const data = {
    metadata,
  };

  const projectType = getState('selected.tipo');
  const format = getState('selected.formato');
  const formatDefault = getState('selected.formatoDefault');

  if (projectType.isGlobal) {
    data.editor_project_types_default_pointer = projectType.objectId;
  } else {
    data.editor_project_types_pointer = projectType.objectId;
  }

  // Se c'e' un formato custom salvo il pointer
  if (format) {
    data.editor_project_formats_pointer = format.objectId;
  }

  if (formatDefault) {
    data.editor_project_formats_default_pointer = formatDefault.objectId;
  }

  let tableName;
  const isGlobalEditor = await isLoggedUserGlobalEditor();

  if (isGlobalEditor) {
    tableName = 'editor_templates_default';
  } else {
    tableName = 'editor_templates';
  }

  let url;
  let method;

  if (firstSave) {
    url = `${process.env.PARSE_API_URL}/${tableName}`;
    method = 'POST';
  } else {
    url = `${process.env.PARSE_API_URL}/${tableName}/${window.editorLoader.templateId}`;
    method = 'PUT';
  }

  const response = await httpClient({
    url,
    method,
    data: { data },
    responseType: 'json',
  });
  const templateId = response.data.data.objectId;

  // I have saved the canvas, now I will save all the pages
  const pagesIds = await saveTemplatePages(templateId, firstSave);

  // Now that I have saved each page and the canvas, I create a relation from
  // the canvas and the pages
  await createTemplateAndPagesRelation(templateId, pagesIds);
};

export const fetchTemplateById = async (
  id,
  { updateSelection = true } = {}
) => {
  const httpClient = await getAxiosClient(true);

  const pointersToInclude = [
    'editor_project_formats_default_pointer',
    'editor_project_formats_pointer',
    'editor_project_templates_default_pointer',
    'editor_project_templates_pointer',
    'editor_project_types_default_pointer',
    'editor_project_types_pointer',
    'editor_templates_categories_default_pointer',
    'editor_templates_categories_pointer',
  ];

  const payload = {};
  let tableName;
  const isGlobalEditor = await isLoggedUserGlobalEditor();

  if (isGlobalEditor) {
    payload.isGlobal = true;
    tableName = 'editor_templates_default';
  } else {
    tableName = 'editor_templates';
  }

  const response = await httpClient.get(
    `${process.env.PARSE_API_URL}/${tableName}`,
    {
      params: {
        filterEqual_objectId: id,
        include: pointersToInclude.join(','),
      },
    }
  );

  if (!response.data.data[0]) {
    const error = new Error('Template not found');
    return error;
  }
  const obj = response.data.data[0];

  if (!obj) {
    // not found
    const error = new Error('Template not found');
    return error;
  }

  if (!updateSelection) {
    return obj;
  }

  modifyState('selected.template', obj);

  // Tipo - Default
  if (obj.editor_project_types_default_pointer) {
    modifyState('selected.tipo', obj.editor_project_types_default_pointer);
  }

  // Tipo - Custom
  if (obj.editor_project_types_pointer) {
    modifyState('selected.tipo', obj.editor_project_types_pointer);
  }

  // Formato - Default
  if (obj.editor_project_formats_default_pointer) {
    modifyState(
      'selected.formatoDefault',
      obj.editor_project_formats_default_pointer
    );
  } else {
    modifyState('selected.formatoDefault', undefined);
  }

  // Formato - Custom
  if (obj.editor_project_formats_pointer) {
    modifyState('selected.formato', obj.editor_project_formats_pointer);
  } else {
    modifyState('selected.formato', {});
  }

  // Categoria Modello - Default
  if (obj.editor_templates_categories_default_pointer) {
    modifyState(
      'selected.templatesCategory',
      obj.editor_templates_categories_default_pointer
    );
  }

  // Categoria Modello - Custom
  if (obj.editor_templates_categories_pointer) {
    modifyState(
      'selected.templatesCategory',
      obj.editor_templates_categories_pointer
    );
  }

  return obj;
};

export const getTemplateImportJob = async (jobId) => {
  const client = await getAxiosClient(true);
  const response = await client.get(
    `${process.env.GIOTTO_API_URL}/templates/import-from-file/${jobId}`
  );
  return response.data;
};

export async function waitForTemplateImportJob(
  jobId,
  {
    onStatus,
    pollIntervalMs = TEMPLATE_IMPORT_POLL_INTERVAL_MS,
    timeoutMs = TEMPLATE_IMPORT_TIMEOUT_MS,
  } = {}
) {
  const startedAt = Date.now();

  while (Date.now() - startedAt < timeoutMs) {
    const job = await getTemplateImportJob(jobId);
    if (onStatus) onStatus(job);

    if (job.status === 'succeeded') {
      return job.result;
    }

    if (job.status === 'failed') {
      const payload = job.error || {};
      const error = new Error(payload.message || 'Importazione fallita');
      error.code = payload.error || 'import_failed';
      error.details = payload.details;
      error.jobId = jobId;
      throw error;
    }

    await wait(pollIntervalMs);
  }

  const error = new Error(
    'Importazione ancora in corso dopo il tempo massimo di attesa'
  );
  error.code = 'import_timeout';
  error.jobId = jobId;
  throw error;
}

/**
 * Import a Publisher .pub file and create (or populate) a Giotto template.
 * Calls the Lambda importTemplateFromFile endpoint.
 *
 * @param {object} params
 * @param {{ url: string, name: string, sizeBytes: number }} params.sourceFile
 * @param {string=} params.templateId - for Flow B (existing template)
 * @param {string=} params.name - template name (Flow A)
 * @param {string=} params.projectTypeId - required for Flow A
 * @param {boolean=} params.projectTypeIsDefault - whether selected project type is default/global
 * @param {string=} params.formatId - selected format override for Flow A
 * @param {boolean=} params.formatIsDefault - whether selected format is default
 * @param {{ dpi?: number, multiplier?: number }=} params.formatOverrides
 * @returns {Promise<{ templateId, formatId, formatIsDefault, formatName, pagesCount, pagesIds, newTemplate, warnings }>}
 */
export const importTemplateFromFile = async (
  {
    sourceFile,
    templateId,
    name,
    projectTypeId,
    projectTypeIsDefault,
    categoryId,
    categoryName,
    categoryIsDefault,
    formatId,
    formatIsDefault,
    formatOverrides,
  },
  options = {}
) => {
  const client = await getAxiosClient(true);
  try {
    const response = await client.post(
      `${process.env.GIOTTO_API_URL}/templates/import-from-file`,
      {
        sourceFile,
        templateId,
        name,
        projectTypeId,
        projectTypeIsDefault,
        categoryId,
        categoryName,
        categoryIsDefault,
        formatId,
        formatIsDefault,
        formatOverrides,
      }
    );

    if (!response.data?.jobId) {
      return response.data;
    }

    return waitForTemplateImportJob(response.data.jobId, options);
  } catch (err) {
    const serverError = err.response?.data;
    if (serverError?.error) {
      const e = new Error(serverError.message || serverError.error);
      e.code = serverError.error;
      e.details = serverError.details;
      throw e;
    }
    throw err;
  }
};
